<template>
  <section class="RL-IsoGallery">
    <!-- Left here for debugging.
    <span v-if="galleryCategories.length">Categories: </span>
    <button v-for="(category, index) in galleryCategories"
            @click="filterGallery(category)">
      {{ category }}
    </button>
    -->
    <gallery :images="gallerySrcs"
             :index="galleryIndex"
             @close="galleryIndex = null"
             @onslide="onSlide"
             ref="gallery">
    </gallery>
    <isotope :item-selector="'RL-IsoGallery__tile'"
             :options="isotopeOptions"
             :list="tiles"
             ref="isotope">
      <a v-for="tile in tiles"
         v-images-loaded="imageLoaded"
         :key="tile.id"
         :class="tile.isBroken"
         @click.prevent="openGallery(tile)"
         href="#">
        <img :src="tile.thumbnail" :alt="tile.alt">
      </a>
      <div :key="'$ignore'" class="RL-IsoGallery__gutter"></div>
    </isotope>
    <mugen-scroll class="RL-IsoGallery__infinite"
                  :class="{'RL-IsoGallery__infinite--hide': hideInfiniteLoading}"
                  :handler="loadMore"
                  :should-handle="!newImagesLoading"
                  ref="mugenScroll">
      <div class="RL-IsoGallery__loading" aria-label="Loading..."></div>
    </mugen-scroll>
  </section>
</template>

<script>
  /* eslint-disable no-plusplus, no-bitwise, no-console */

  import isotope from 'vueisotope';
  import imagesLoaded from 'vue-images-loaded';
  import gallery from 'vue-gallery';
  import mugenScroll from 'vue-mugen-scroll';

  export default {
    name: 'iso-gallery',
    components: {
      isotope,
      gallery,
      mugenScroll,
    },
    directives: {
      imagesLoaded,
    },
    data() {
      return {
        hostUrl: '',
        nextId: 0,
        galleryIndex: null,
        newImagesLoading: true, // Starts as true, for we need to load the JSON data fist.
        hideInfiniteLoading: false,
        rawData: {
          data: null,
          categoriedIndexes: [],
        },
        tiles: [],
        filterByThisCategory: 0,
        isotopeOptions: {
          itemSelector: '.RL-IsoGallery__tile',
          percentPosition: true,
          masonry: {
            gutter: '.RL-IsoGallery__gutter',
          },
          getFilterData: [
            tile => ~tile.categories.indexOf(this.filterByThisCategory),
          ],
          getSortData: {
            id: 'id',
          },
        },
      };
    },
    computed: {
      filteredTiles() {
        return this.tiles
          .filter(tile => ~tile.categories.indexOf(this.filterByThisCategory));
      },
      gallerySrcs() {
        return this.filteredTiles
          .map(tile => tile.src);
      },
      galleryCaptions() {
        return this.filteredTiles
          .map(tile => tile.caption);
      },
      /**
       * Loops through all the images, checking for their category IDs,
       * merging them into one array with unique values. Used for
       * debugging. Also uncomment lines 4-8.
       *
       * A mix of these two:
       * https://stackoverflow.com/a/33512273
       * https://stackoverflow.com/a/39346978
       *
       * @return {array} Array of category ids.
       */
      /*
      galleryCategories() {
        // Yeah.
        return [...new Set([].concat(...this.tiles.map(tile => tile.categories)))];
      },
      */
    },
    methods: {
      /**
       * Get JSON data from WordPress.
       */
      loadJSON() {
        this.hostUrl = window.location.hostname === 'localhost'
          ? 'https://easy-visual.local'
          : '';
        // @todo @alvar remove when ready for production. Not breaking.
        const temporaryUrlCheck = ~window.location.pathname.indexOf('photography-new') ? '/photography-new' : '';
        const lang = document.documentElement.lang;
        // Default 'picked' category.
        this.filterByThisCategory = lang === 'fi'
          ? 41 // Finnish 'Valitut'
          : 43; // English 'Picked'
        const jsonPath = '/wp-json/wp/v2/gallery-image?per_page=99999';
        const docLang = `&lang=${lang}`;
        this.$http.get(this.hostUrl + temporaryUrlCheck + jsonPath + docLang).then((response) => {
          if (response.data && response.data) {
            this.parseRawData(response.data);
          }
        });
      },
      /**
       * Parses the WordPress JSON data into an array matrix with
       * category IDs as keys, pointing to image wrapper IDs.
       */
      parseRawData(data) {
        if (data.constructor === Array && data.length) {
          this.rawData.data = data;
          const categoriedIndexes = {};

          // Sort data indexes into categories
          data.forEach((el, i) => {
            el.categories.forEach((cat) => {
              categoriedIndexes[cat] = categoriedIndexes[cat]
                ? categoriedIndexes[cat]
                : [];
              categoriedIndexes[cat].push(i);
            });
          });

          this.rawData.categoriedIndexes = categoriedIndexes;
          this.newImagesLoading = 0;
          this.loadMore();
        } else {
          // Hide loading circle if no data to parse. (@todo @alvar, can be made clearer?)
          this.hideInfiniteLoading = true;
          // eslin
          console.error('IsoGallery: No data to parse from JSON.');
        }
      },
      /**
       * Checks which images we need to load next from the JSON data.
       */
      loadMore() {
        const indexes = this.rawData.categoriedIndexes[this.filterByThisCategory] || [];
        let imagesToLoad = 10;
        imagesToLoad = indexes.length < imagesToLoad
          ? indexes.length
          : imagesToLoad;

        while (imagesToLoad && indexes.length) {
          const item = this.rawData.data[indexes[0]];
          let image = null;
          let src = '';
          let thumbnail = '';
          // Check if the item has an image, and if it has been loaded.
          try {
            image = item.acf.gallery_image;
            src = item.acf.gallery_image.sizes['gallery-image'];
            thumbnail = item.acf.gallery_image.sizes['gallery-thumbnail'];
          } catch (e) {
            // Just trying, not catching!
            // console.log('skipped');
          }

          if (src) {
            this.newImagesLoading++;
            // Add image to load queue.
            this.tiles.push({
              id: this.nextId++,
              src: this.hostUrl + src,
              thumbnail: this.hostUrl + thumbnail,
              alt: image.alt,
              caption: image.caption,
              categories: item.categories,
            });

            // Deleting image data, as we only need to download it once.
            delete this.rawData.data[indexes[0]];
            // The image has been added to load queue.
            imagesToLoad--;
          }

          // Remove index from category, as the image has been received
          // for loading (now or before from an other category).
          indexes.shift();
        }

        // No more need to load images for this category.
        if (!indexes.length) {
          this.hideInfiniteLoading = true;
        }
      },
      /**
       * This is called when an image is loaded.
       */
      imageLoaded(instance) {
        const instanceElement = instance.elements[0];
        if (instance.hasAnyBroken) {
          // eslint-disable-next-line no-underscore-dangle
          const brokenIndex = instanceElement.__underlying_element.index;
          this.tiles[brokenIndex].alt = '';
          this.tiles[brokenIndex].isBroken = 'is-broken';
        }
        this.$refs.isotope.layout();
        // for mobile
        if (!instance.hasAnyBroken) {
          instanceElement.classList.add('is-visible');
        }
        // If lightbox gallery is currently open, refresh it.
        this.refreshGallery();
        // This is for checking if we still need more images
        // especially on first load (to fill the screen).
        if (!--this.newImagesLoading && this.isInOrAboveViewport(this.$refs.mugenScroll.$el)) {
          this.loadMore();
        }
      },
      /**
       * Filter gallery and tiles.
       */
      filterGallery(category) {
        // If the category is already the same or not an interger, don't filter the list.
        if (category === this.filterByThisCategory || !Number.isInteger(category)) {
          return;
        }
        this.filterByThisCategory = category;
        this.$refs.isotope.filter(0);
        // Check if we need more images after filtering (to fill the screen).
        if (!this.newImagesLoading) {
          // Show infinite loader.
          this.hideInfiniteLoading = false;
          // Check if we need more tiles
          if (this.tiles.length < 10 || this.isInOrAboveViewport(this.$refs.mugenScroll.$el)) {
            this.loadMore();
          }
        }
      },
      /**
       * Open the lightbox gallery.
       */
      openGallery(tile) {
        const galleryIndex = this.gallerySrcs.indexOf(tile.src);
        this.galleryIndex = galleryIndex !== -1 ? galleryIndex : tile.id;
      },
      /**
       * Fires when sliding (and opening) the lightbox gallery.
       *
       * Check if the current open lightbox image has a caption, and applies it.
       * Also, check if we need to load more images.
       */
      onSlide(e) {
        const captionTarget = e.slide;
        if (captionTarget && !captionTarget.getAttribute('data-caption')) {
          const dataIndex = captionTarget.getAttribute('data-index');
          const caption = this.galleryCaptions[dataIndex];
          if (caption) {
            captionTarget.setAttribute('data-caption', caption);
          }
        }

        // When the last slide is within 3 indexes of the current slide, try to load more.
        if (!this.newImagesLoading && (e.index + 3 >= this.filteredTiles.length)) {
          this.loadMore();
        }
      },
      /**
       * Toggles the visibility of a lightbox image's caption.
       */
      toggleCaption(e) {
        let captionTarget;

        if (e.target.classList.contains('slide')) {
          captionTarget = e.target;
        } else if (e.target.classList.contains('slide-content')) {
          captionTarget = e.target.parentNode;
        }

        if (captionTarget && captionTarget.getAttribute('data-caption')) {
          captionTarget.classList.toggle('show-info');
        }
      },
      /**
       * Refresh the lightbox gallery with new image data.
       */
      refreshGallery() {
        if (this.galleryIndex === null) {
          return;
        }

        const galleryInstance = this.$refs.gallery.instance;

        if (galleryInstance && galleryInstance.list.length !== this.gallerySrcs.length) {
          const newItems = this.gallerySrcs
            .filter(item => !~galleryInstance.list.indexOf(item));
          galleryInstance.add(newItems);
        }
      },
      /**
       * Modified from https://gist.github.com/jjmu15/8646226
       * checking if element top is in or above viewport.
       *
       * @param  {object}  Dom Element
       * @return {Boolean} true if in viewport
       */
      isInOrAboveViewport(element) {
        const rect = element.getBoundingClientRect();
        const html = document.documentElement;
        return (
          rect.top < (window.innerHeight || html.clientHeight) &&
          rect.left >= 0 &&
          rect.right <= (window.innerWidth || html.clientWidth)
        );
      },
    },
    created() {
      this.loadJSON();
    },
    mounted() {
      document.getElementById('blueimp-gallery').addEventListener('click', this.toggleCaption);
      const self = this;
      // Add gallery filtering end-point for WordPress
      window.filterIsoGallery = function filterIsoGallery(category) {
        self.filterGallery(category);
      };
    },
    beforeDestroy() {
      document.getElementById('blueimp-gallery').removeEventListener('click', this.toggleCaption);
    },
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>

  * {
    outline-color: $color_turquoise;
  }

  .RL-IsoGallery {
    text-align: center;
  }

  .RL-IsoGallery__tile {
    visibility: hidden;
    opacity: 0;
    width: 100%;
    margin-top: 0.6rem;
    box-sizing: border-box;
    transition: opacity 0.5s; // Mostly for mobile
    // background-color: red; @todo @alvar remove, for testing.

    @media screen and (min-width: 576px) {
      width: (100% - 2 * $masonry_gutter_width) / 3;
      margin-top: $masonry_gutter_width;
    }

    @media screen and (min-width: 1024px) {
      width: (100% - 4 * $masonry_gutter_width) / 5;
    }

    > img {
      display: block;
      width: 100%;
    }

    &.is-visible {
      // both visible & opacity for mobile
      visibility: visible;
      opacity: 1;
    }

    &.is-broken {
      height: 0;
      margin-top: 0;
      overflow: none;
    }

    &:hover,
    &:focus {
      opacity: 0.7;
    }
  }

  .RL-IsoGallery__gutter {
    width: $masonry_gutter_width;
    height: 0;
  }

  .RL-IsoGallery__infinite {
    position: relative;
    padding-top: 64vh;
    transform: translateY(-32vh);
    transition: 1s padding;
    // z-index: -1;
    // margin-bottom: 4em; <main> padded in wp theme
  }

  .RL-IsoGallery__infinite--hide {
    visibility: hidden;
    padding-top: 0;
  }

  // https://projects.lukehaas.me/css-loaders/
  .RL-IsoGallery__loading {
    display: inline-block;
    margin: 0 auto;
    font-size: 1em;
    position: relative;
    text-indent: -9999em;
    border-top: 1.2em solid $color_gray-light;
    border-right: 1.2em solid $color_gray-light;
    border-bottom: 1.2em solid $color_gray-light;
    border-left: 1.2em solid $color_turquoise;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: loading-animation 1.1s infinite linear;
    animation: loading-animation 1.1s infinite linear;

    &,
    &:after {
      border-radius: 50%;
      width: 4em;
      height: 4em;
    }
  }

  @-webkit-keyframes loading-animation {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }

    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }

  @keyframes loading-animation {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }

    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>
<style lang="scss">
  .blueimp-gallery > .slides > .slide[data-caption] {
    cursor: pointer;
    &::before {
      content: 'i';
      opacity: 0.5;
      position: absolute;
      right: 0;
      bottom: 0;
      z-index: 1;
      margin-right: 1%;
      margin-bottom: 1%;
      font-weight: 700;
      box-sizing: border-box;
      border-radius: 50% 50%;
      background: $color_black;
      color: $color_white;
      transition: 0.3s;
    }
    &::after {
      content: attr(data-caption);
      opacity: 0;
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      padding: 1em 10%;
      font-size: 2em;
      line-height: 1.5;
      text-align: left;
      background: $color_black;
      color: $color_white;
      transition: 0.3s;
    }
    &.show-info {
      &::before {
        opacity: 0;
      }
      &::after {
        opacity: 0.8;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/IsoGallery.vue